import { ComponentType, useEffect, useMemo, useState } from 'react'

import { Box, Button, Text, Title } from '@factorialco/deprecated-design-system'
import { F0TableOfContent, TOCItem } from '@factorialco/f0-react/dist/experimental'
import {
  ErrorDetailsFragment,
  ProductSettingsPageDocument,
  SaveProductSettingsDocumentDocument,
  SaveProductSettingsRecordDocument,
} from 'generated/resources/graphql'
import startCase from 'lodash/startCase'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

import TextArea from 'modules/core/components/TextArea'
import PageLoading from 'modules/layout/components/Page/Loading'

// Plugin contract for `record` settings. Each product supplies a
// component per record setting. The component is fully controlled: it
// receives the current value and emits changes through `onChange`.
// Values are free-shape objects — they must match the backend
// `T::Struct` but we don't type that relationship on the frontend
// (yet). Backend validation is the source of truth.
export type RecordViewProps = {
  value: unknown
  onChange: (next: unknown) => void
  disabled?: boolean
  errors?: string[]
}

export type RecordViewComponent = ComponentType<RecordViewProps>

export type RecordsComponentMap = Record<string, RecordViewComponent>

// Plugin contract for `list` settings. Reads stay with the consumer
// (their own GraphQL resource). The framework only routes the list
// metadata down — the consumer fetches, renders, and wires mutations
// through `useProductSettingsListMutations`.
export type ListViewProps = {
  productId: string
  listName: string
  companyId: string
}

export type ListViewComponent = ComponentType<ListViewProps>

export type ListsComponentMap = Record<string, ListViewComponent>

// Plugin contract for `ai_structure` settings. Reads stay with the
// consumer (their own GraphQL resource) and so does rendering — the
// framework only routes routing identifiers and the manifest-declared
// JSON Schema down. The consumer wires saves through
// `useProductSettingsAiStructureSave`, which targets the framework's
// `save` verb.
export type AiStructureViewProps = {
  productId: string
  settingName: string
  companyId: string
  // Manifest-declared JSON Schema as a string (the same value the
  // backend uses to validate saves). Null only if shadowdog hasn't
  // caught up yet; in practice the framework always populates it for
  // ai_structure settings.
  schemaJson: string | null
}

export type AiStructureViewComponent = ComponentType<AiStructureViewProps>

export type AiStructuresComponentMap = Record<string, AiStructureViewComponent>

// Outcome the framework reports after a record/document setting save.
// Wired to a callback so consumers can hook up their own success/error
// feedback (flash message, telemetry, custom UI) without forcing this
// foundational component onto a specific notification library.
export type SaveOutcome = { success: true } | { success: false; errors: string[] }

interface Props {
  productId: string
  recordsComponentMap: RecordsComponentMap
  listsComponentMap?: ListsComponentMap
  aiStructuresComponentMap?: AiStructuresComponentMap
  // Optional. Called once per record-/document-setting save attempt,
  // after the mutation resolves. Consumers typically use it to render
  // a toast / flash message. The framework itself stays headless and
  // dependency-free of any notification mechanism.
  onSaveOutcome?: (outcome: SaveOutcome & { settingName: string }) => void
}

type SettingNode = {
  id: string
  name: string
  kind: string
  description: string
  authorized: boolean
  schemaJson?: string | null
}

type RecordNode = {
  settingName: string
  valueJson: string
}

type DocumentNode = {
  settingName: string
  content: string
}

type ParsedValue = { ok: true; value: unknown } | { ok: false; error: string }

const parseValue = (valueJson: string | null | undefined): ParsedValue => {
  if (valueJson == null) return { ok: true, value: undefined }
  try {
    return { ok: true, value: JSON.parse(valueJson) }
  } catch (e) {
    return {
      ok: false,
      error: e instanceof Error ? e.message : 'invalid JSON',
    }
  }
}

const ProductSettingsPage = ({
  productId,
  recordsComponentMap,
  listsComponentMap,
  aiStructuresComponentMap,
  onSaveOutcome,
}: Props) => {
  const { company } = useCurrentSession()

  const { data, loading } = useQuery(ProductSettingsPageDocument, {
    variables: { productId, companyId: company.id },
  })

  const [saveRecord, { loading: savingRecord }] = useMutation(SaveProductSettingsRecordDocument, {
    extractorFn: (data) => data.productSettingsMutations.saveProductsettingsRecord,
  })

  const [saveDocument, { loading: savingDocument }] = useMutation(
    SaveProductSettingsDocumentDocument,
    {
      extractorFn: (data) => data.productSettingsMutations.saveProductsettingsDocument,
    }
  )

  const saving = savingRecord || savingDocument

  const nodes = useMemo<SettingNode[]>(() => data?.productSettings?.definitions ?? [], [data])

  const recordNodes = useMemo<RecordNode[]>(() => data?.productSettings?.records ?? [], [data])

  const documentNodes = useMemo<DocumentNode[]>(
    () => data?.productSettings?.documents ?? [],
    [data]
  )

  // Parse each record's stored JSON once. Successful parses feed the
  // controlled fields; failures are tracked separately so we can both
  // surface the parse error in the consumer's `errors` prop AND
  // disable Save for that setting (otherwise clicking Save would
  // re-save `{}` on top of the malformed backend value, silently
  // discarding it).
  const parsedByName = useMemo<Record<string, ParsedValue>>(() => {
    const acc: Record<string, ParsedValue> = {}
    recordNodes.forEach((record) => {
      acc[record.settingName] = parseValue(record.valueJson)
    })
    return acc
  }, [recordNodes])

  const backendInitial = useMemo<Record<string, unknown>>(() => {
    const initial: Record<string, unknown> = {}
    recordNodes.forEach((record) => {
      const parsed = parsedByName[record.settingName]
      initial[record.settingName] = parsed?.ok ? parsed.value : undefined
    })
    return initial
  }, [recordNodes, parsedByName])

  // Documents are plain text — no JSON parse, no struct shape. Keep
  // the same "backend value indexed by setting name" pattern so the
  // edit/save bookkeeping below applies uniformly across kinds.
  const backendDocumentInitial = useMemo<Record<string, string>>(() => {
    const initial: Record<string, string> = {}
    documentNodes.forEach((doc) => {
      initial[doc.settingName] = doc.content
    })
    return initial
  }, [documentNodes])

  // In-flight, unsaved field edits. Preserved across TOC navigation
  // (a user editing setting A, glancing at setting B, then coming
  // back to A still sees their unsaved value); cleared per setting
  // only on a successful save of that setting.
  const [edits, setEdits] = useState<Record<string, unknown>>({})
  const [savingName, setSavingName] = useState<string | null>(null)

  // Per-setting validation errors from the most recent failed save.
  // Routed into `RecordViewProps.errors` so the consumer field can
  // highlight invalid input alongside the consumer-supplied toast.
  // Cleared per setting when the user edits the field again, or on
  // a successful subsequent save.
  const [saveErrors, setSaveErrors] = useState<Record<string, string[]>>({})

  const handleFieldChange = (settingName: string) => (next: unknown) => {
    setEdits((prev) => ({ ...prev, [settingName]: next }))
    setSaveErrors((prev) => {
      if (prev[settingName] == null) return prev
      const { [settingName]: _removed, ...rest } = prev
      return rest
    })
  }

  const finalizeSave = (
    settingName: string,
    handled: { success: true } | { success: false; errors: ReadonlyArray<ErrorDetailsFragment> }
  ) => {
    if (handled.success) {
      setEdits((prev) => {
        const { [settingName]: _removed, ...rest } = prev
        return rest
      })
      setSaveErrors((prev) => {
        if (prev[settingName] == null) return prev
        const { [settingName]: _removed, ...rest } = prev
        return rest
      })
      onSaveOutcome?.({ success: true, settingName })
    } else {
      const errors = handled.errors.map((e) => {
        if (e.__typename === 'StructuredError') {
          return `${e.field}: ${e.messages.join(', ')}`
        }
        if (e.__typename === 'SimpleError') {
          return e.message
        }
        return 'Unknown error'
      })
      setSaveErrors((prev) => ({ ...prev, [settingName]: errors }))
      onSaveOutcome?.({ success: false, settingName, errors })
    }
  }

  const handleSaveRecord = (settingName: string) => async () => {
    const value = edits[settingName] ?? backendInitial[settingName]
    setSavingName(settingName)

    const result = await saveRecord({
      variables: {
        productId,
        companyId: company.id,
        settingName,
        valueJson: JSON.stringify(value ?? {}),
      },
      refetchQueries: [ProductSettingsPageDocument],
    })

    finalizeSave(
      settingName,
      handleResult(result, () => {})
    )
    setSavingName(null)
  }

  const handleSaveDocument = (settingName: string) => async () => {
    const value = edits[settingName] ?? backendDocumentInitial[settingName] ?? ''
    setSavingName(settingName)

    const result = await saveDocument({
      variables: {
        productId,
        companyId: company.id,
        settingName,
        content: typeof value === 'string' ? value : '',
      },
      refetchQueries: [ProductSettingsPageDocument],
    })

    finalizeSave(
      settingName,
      handleResult(result, () => {})
    )
    setSavingName(null)
  }

  // Unified, ordered list of settings the consumer can render —
  // the menu draws from this and the right pane mounts whichever
  // entry matches the current selection.
  const renderable = useMemo(
    () =>
      nodes.filter((node) => {
        if (!node.authorized) return false
        if (node.kind === 'record') return recordsComponentMap[node.name] != null
        if (node.kind === 'list') return listsComponentMap?.[node.name] != null
        if (node.kind === 'ai_structure') return aiStructuresComponentMap?.[node.name] != null
        // Documents are rendered by the framework itself (built-in
        // textarea), so they don't need a consumer-supplied component.
        if (node.kind === 'document') return true
        return false
      }),
    [nodes, recordsComponentMap, listsComponentMap, aiStructuresComponentMap]
  )

  const [selectedName, setSelectedName] = useState<string | null>(null)

  // Default-select the first renderable setting once data lands; if the
  // selected entry disappears (e.g. after refetch) fall back to the
  // first available one.
  useEffect(() => {
    if (renderable.length === 0) {
      if (selectedName !== null) setSelectedName(null)
      return
    }
    const stillThere = renderable.some((n) => n.name === selectedName)
    if (!stillThere) {
      const first = renderable[0]
      if (first) setSelectedName(first.name)
    }
  }, [renderable, selectedName])

  if (loading && !data) {
    return <PageLoading />
  }

  const selectedNode = renderable.find((n) => n.name === selectedName) ?? null

  const tocItems: TOCItem[] = renderable.map((node) => ({
    id: node.name,
    label: startCase(node.name),
    onClick: (id) => setSelectedName(id),
  }))

  return (
    <div className="flex gap-6 items-start">
      <aside className="shrink-0 w-[260px]">
        {renderable.length === 0 ? (
          <Text.Normal color="grey800">
            {i18n.t('product_settings.framework.empty_state')}
          </Text.Normal>
        ) : (
          <F0TableOfContent
            title={startCase(productId)}
            items={tocItems}
            activeItem={selectedName ?? undefined}
            scrollable={false}
          />
        )}
      </aside>
      <main className="flex-1 min-w-0">{selectedNode && renderSetting(selectedNode)}</main>
    </div>
  )

  function renderSetting(node: SettingNode) {
    if (node.kind === 'record') {
      const Field = recordsComponentMap[node.name]
      if (!Field) return null
      const value = edits[node.name] ?? backendInitial[node.name]
      const isSavingThis = saving && savingName === node.name
      const parsed = parsedByName[node.name]
      // If the backend value is malformed JSON, surface that to the
      // field as an error and disable Save — otherwise the field
      // would render as if the value were `{}` and a Save click
      // would silently overwrite the malformed row with `{}`.
      const malformedError =
        parsed && !parsed.ok ? [`malformed backend value: ${parsed.error}`] : []
      const errors = [...(saveErrors[node.name] ?? []), ...malformedError]
      const hasMalformedValue = parsed != null && !parsed.ok
      return (
        <Box key={node.name} gap="s12">
          <Field
            value={value}
            onChange={handleFieldChange(node.name)}
            disabled={isSavingThis || hasMalformedValue}
            errors={errors.length > 0 ? errors : undefined}
          />
          <Button
            onClick={handleSaveRecord(node.name)}
            disabled={isSavingThis || hasMalformedValue}
          >
            {i18n.t('product_settings.framework.save')}
          </Button>
        </Box>
      )
    }

    if (node.kind === 'list' && listsComponentMap) {
      const View = listsComponentMap[node.name]
      if (!View) return null
      return (
        <View
          key={node.name}
          productId={productId}
          listName={node.name}
          companyId={String(company.id)}
        />
      )
    }

    if (node.kind === 'ai_structure' && aiStructuresComponentMap) {
      const View = aiStructuresComponentMap[node.name]
      if (!View) return null
      return (
        <View
          key={node.name}
          productId={productId}
          settingName={node.name}
          companyId={String(company.id)}
          schemaJson={node.schemaJson ?? null}
        />
      )
    }

    if (node.kind === 'document') {
      const stagedValue = edits[node.name]
      const value =
        typeof stagedValue === 'string' ? stagedValue : (backendDocumentInitial[node.name] ?? '')
      const isSavingThis = saving && savingName === node.name
      const errors = saveErrors[node.name] ?? []
      const label = startCase(node.name)

      return (
        <Box key={node.name} flexGrow gap="s16">
          <Title variant="400" title={label} description={node.description} />
          <TextArea
            label={label}
            hideLabel
            value={value}
            onChange={(next) => handleFieldChange(node.name)(next)}
            minRows={14}
            error={errors.length > 0 ? errors.join('\n') : undefined}
            readonly={isSavingThis}
          />
          <Box alignSelf="flexStart">
            <Button onClick={handleSaveDocument(node.name)} disabled={isSavingThis}>
              {i18n.t('product_settings.framework.save')}
            </Button>
          </Box>
        </Box>
      )
    }

    return null
  }
}

export default ProductSettingsPage
